import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { includesFolded } from "../utils/text";
import "./SearchableSelect.css";

interface Option {
  value: string;
  label: string;
}

interface SearchableSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: Option[];
  /** Optional server-side search handler. If omitted, options are filtered locally. */
  onSearch?: (query: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  /** Keep the dropdown open after a selection (repeat-add pickers). */
  closeOnSelect?: boolean;
}

export default function SearchableSelect({
  value,
  onChange,
  onSearch,
  options,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  disabled = false,
  closeOnSelect = true,
}: SearchableSelectProps) {
  const { t } = useTranslation();
  const ph = placeholder ?? t("common.selectPh");
  const searchPh = searchPlaceholder ?? t("common.searchPh");
  const emptyMsg = emptyMessage ?? t("common.noResults");
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedLabel = options.find((o) => o.value === value)?.label || "";

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const filteredOptions = onSearch
    ? options
    : options.filter((o) => includesFolded(o.label, query));

  useEffect(() => {
    if (!onSearch || !query.trim()) return;
    const timeout = setTimeout(() => {
      onSearch(query);
    }, 200);
    return () => clearTimeout(timeout);
  }, [query, onSearch]);

  return (
    <div ref={containerRef} className="searchable-select">
      <button
        type="button"
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
        className="searchable-select-trigger"
      >
        <span className="searchable-select-value">
          {selectedLabel || ph}
        </span>
        <span className="searchable-select-arrow">{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div className="searchable-select-dropdown">
          <div className="searchable-select-search">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPh}
            />
          </div>
          <div className="searchable-select-options">
            {filteredOptions.length === 0 ? (
              <div className="searchable-select-empty">{emptyMsg}</div>
            ) : (
              filteredOptions.map((o) => (
                <div
                  key={o.value}
                  onClick={() => {
                    onChange(o.value);
                    setQuery("");
                    if (closeOnSelect) {
                      setOpen(false);
                    } else {
                      inputRef.current?.focus();
                    }
                  }}
                  className={`searchable-select-option${o.value === value ? " selected" : ""}`}
                >
                  {o.label}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
